import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ListData } from '@brightside/brightside-ui';
import Fuse from 'fuse.js';
import { Observable, shareReplay, tap } from 'rxjs';
import { map } from 'rxjs/operators';

export interface SocJsonItem {
  title: string;
  code: string;
}

const FUSE_OPTIONS = {
  keys: ['displayName'],
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 1,
};

@Injectable({
  providedIn: 'root',
})
export class SocProfessionListService {
  private readonly _http = inject(HttpClient);
  private _fuse: Fuse<ListData> | null = null;
  private readonly _codeToTitle = new Map<string, string>();

  readonly list$: Observable<ListData[]> = this._http.get<SocJsonItem[]>('assets/soc.json').pipe(
    map((items) =>
      items.map((item) => {
        this._codeToTitle.set(item.code, item.title);
        return { displayName: item.title, code: item.code } as ListData;
      })
    ),
    tap((list) => {
      this._fuse = new Fuse(list, FUSE_OPTIONS);
    }),
    shareReplay(1)
  );

  searchByProfessionQuery(query: string, maxResults = 50): ListData[] {
    const q = query?.trim() ?? '';
    if (!q || !this._fuse) {
      return [];
    }
    return this._fuse.search(q).slice(0, maxResults).map((r) => r.item);
  }

  getTitleForCode(code: string | undefined | null): string | undefined {
    if (!code) {
      return undefined;
    }
    return this._codeToTitle.get(code);
  }

  /** Value sent to the API: `{code} - {title}`. */
  formatProfessionSubmissionValue(code: string, title: string): string {
    return `${code} - ${title}`;
  }

  /** Human-readable title from a stored code or `{code} - {title}` submission value. */
  getDisplayTitleFromValue(value: string | undefined | null): string | undefined {
    if (!value) {
      return undefined;
    }
    const separatorIndex = value.indexOf(' - ');
    if (separatorIndex !== -1) {
      return value.slice(separatorIndex + 3);
    }
    return this.getTitleForCode(value) ?? value;
  }
}
